import React, { useCallback, useEffect, useState } from 'react';
import { FormControl } from '@primer/react';
import { MailIcon } from '@primer/octicons-react';

import every from 'lodash/every';
import isFunction from 'lodash/isFunction';
import some from 'lodash/some';
import times from 'lodash/times';

import { isEmail } from '../../common/TextInputs/EmailInput/email-utils';

import Button from '../../common/Button/Button';
import CardHeader from '../../common/Labels/CardHeader';
import CardView from '../../common/CardView/CardView';
import EmailInput from '../../common/TextInputs/EmailInput/EmailInput';
import Icon from '../../common/icons/Icon';
import { getThemeColor } from '../../../utils/theme-utils';

const IndexedEmailInput = ({
  index,
  onSubmit, 
  onChange,
}) => (
  <EmailInput
    className="px-2 py-1 w-full"
    onChange={event => onChange(event, index)}
    onSubmit={value => onSubmit(value, index)}
    block
  />
)

const isValueReadyForSubmission = value => !value?.length || isEmail(value);
const areAnyValuesEmails = values => some(values, isEmail);
const areValuesReadyForSubmission = values => every(values, isValueReadyForSubmission)
const filterForEmails = values => values.filter(value => value.length && isEmail(value)) 

export const UserInviteForm = ({
  error, onFormSubmit,
}) => {
  const [inputValues, setInputValueArray] = useState(['', '', '', '', '']);
  const [errorMessage, setErrorMessage] = useState(error);
  
  const doesFormHaveErrors = useCallback(() => {
    if (!areAnyValuesEmails(inputValues) || !areValuesReadyForSubmission(inputValues)) {
      return 'Please make sure that all of your entries are valid email addresses'
    }
    return false;
  }, [inputValues])
  const submitValues = useCallback(() => {
    if (isFunction(onFormSubmit)) {
      const emailValues = filterForEmails(inputValues);
      onFormSubmit(emailValues);
    }
  }, [inputValues, onFormSubmit])
  
  const onFormInputChange = useCallback((value, index) => {
    setErrorMessage(null)
    
    const newInputValues = [...inputValues];
    newInputValues[index] = value;
    setInputValueArray(newInputValues);
  }, [inputValues])

  const onFormSubmission = useCallback(() => {
    setErrorMessage(null);
    const formError = doesFormHaveErrors();
    if (formError) {
      setErrorMessage(formError)
    } else {
      submitValues();
    }
  }, [doesFormHaveErrors, submitValues]);

  useEffect(() => {
    if (error) {
      setErrorMessage(error);
    }
  }, [error])

  return (
    <CardView className="items-center justify-between w-full" disableFullHeight>
      <div className="flex flex-col items-start justify-start w-full">
        <div className="mb-2 flex items-center">
          <Icon fill={getThemeColor('brand')} icon={MailIcon} size={24} />
          <CardHeader className="ml-2">Invite Users</CardHeader>
        </div>
        {
          times(5, index => (
            <IndexedEmailInput
              index={index}
              onChange={onFormInputChange}
              onSubmit={onFormSubmission}
            />
          ))
        }
      </div>
      <FormControl>
        <FormControl.Label visuallyHidden>Click to Invite Users</FormControl.Label>
        <Button
          className="mt-4 w-full"
          variant="primary"
          onClick={onFormSubmission}
        >
          Send Invitations
        </Button>
        {
          errorMessage ? (
            <FormControl.Validation variant="error">
              { errorMessage }
            </FormControl.Validation>
          ) : null
        }
      </FormControl>
    </CardView>
  )
}

export default UserInviteForm;
